import React, { useState, useRef, useEffect } from "react";
import { Menu, Send, Bot, User, ShieldCheck, Target, DollarSign, CalendarClock, Phone, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import Sidebar from "@/components/Sidebar";
import { authFetch } from "@/lib/authFetch";
import MarkdownMessage from "@/components/MarkdownMessage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (d) => {
  if (!d) return null;
  try {
    const date = new Date(d);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return d; }
};

const QuickSummaryBanner = ({ summary, isDarkMode }) => {
  if (!summary || !summary.has_crm_data) return null;

  const scores = summary.credit_scores || {};
  const hasScores = scores.experian || scores.equifax || scores.transunion;
  const upcomingAppts = summary.upcoming_appointments || [];
  const responsibilities = summary.client_responsibilities || [];

  const bg = isDarkMode ? "bg-zinc-900 border-zinc-800" : "bg-white border-slate-200";
  const txt = isDarkMode ? "text-white" : "text-slate-900";
  const sub = isDarkMode ? "text-zinc-400" : "text-slate-500";

  return (
    <Card className={`${bg} border mb-4`} data-testid="crm-quick-summary-banner">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#22c55e] to-[#15803d] flex items-center justify-center">
            <Bot className="h-4 w-4 text-white" />
          </div>
          <div>
            <p className={`text-sm font-bold ${txt}`}>Quick Summary — {summary.client_name}</p>
            <p className={`text-xs ${sub}`}>Live CRM data</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {/* Scores */}
          {hasScores && (
            <div className={`rounded-lg p-3 ${isDarkMode ? "bg-zinc-800" : "bg-slate-50"}`}>
              <div className="flex items-center gap-1 mb-1">
                <ShieldCheck className="h-3.5 w-3.5 text-[#22c55e]" />
                <span className={`text-xs font-semibold ${sub}`}>Scores</span>
              </div>
              <div className="flex gap-2">
                {scores.experian && <div className="text-center"><p className={`text-sm font-bold ${txt}`}>{scores.experian}</p><p className="text-[9px] text-slate-400">EXP</p></div>}
                {scores.equifax && <div className="text-center"><p className={`text-sm font-bold ${txt}`}>{scores.equifax}</p><p className="text-[9px] text-slate-400">EQ</p></div>}
                {scores.transunion && <div className="text-center"><p className={`text-sm font-bold ${txt}`}>{scores.transunion}</p><p className="text-[9px] text-slate-400">TU</p></div>}
              </div>
            </div>
          )}

          {/* Status */}
          {(summary.lead_status || summary.loan_ready_stage) && (
            <div className={`rounded-lg p-3 ${isDarkMode ? "bg-zinc-800" : "bg-slate-50"}`}>
              <div className="flex items-center gap-1 mb-1">
                <Target className="h-3.5 w-3.5 text-[#22c55e]" />
                <span className={`text-xs font-semibold ${sub}`}>Status</span>
              </div>
              <p className={`text-sm font-bold ${txt}`}>{summary.loan_ready_stage || summary.lead_status}</p>
              {summary.current_round && <p className={`text-[10px] ${sub}`}>Round {summary.current_round}</p>}
            </div>
          )}

          {/* Payment */}
          {(summary.payment_status || summary.next_payment) && (
            <div className={`rounded-lg p-3 ${isDarkMode ? "bg-zinc-800" : "bg-slate-50"}`}>
              <div className="flex items-center gap-1 mb-1">
                <DollarSign className="h-3.5 w-3.5 text-[#22c55e]" />
                <span className={`text-xs font-semibold ${sub}`}>Payment</span>
              </div>
              {summary.payment_status && (
                <p className={`text-sm font-bold ${summary.payment_status === "Current" ? "text-green-600" : summary.payment_status === "Failed Payment" ? "text-red-600" : txt}`}>
                  {summary.payment_status}
                </p>
              )}
              {summary.next_payment && (
                <p className={`text-[10px] ${sub}`}>${summary.next_payment.amount} due {formatDate(summary.next_payment.due_date)}</p>
              )}
            </div>
          )}

          {/* Appointments */}
          {upcomingAppts.length > 0 && (
            <div className={`rounded-lg p-3 ${isDarkMode ? "bg-zinc-800" : "bg-slate-50"}`}>
              <div className="flex items-center gap-1 mb-1">
                <CalendarClock className="h-3.5 w-3.5 text-[#22c55e]" />
                <span className={`text-xs font-semibold ${sub}`}>Next Appt</span>
              </div>
              <p className={`text-xs font-semibold ${txt} truncate`}>{upcomingAppts[0].title}</p>
              <p className={`text-[10px] ${sub}`}>{formatDate(upcomingAppts[0].start_time)}</p>
            </div>
          )}
        </div>

        {/* Tasks / Responsibilities row */}
        {(responsibilities.length > 0 || summary.case_agent) && (
          <div className="flex flex-wrap gap-3">
            {responsibilities.length > 0 && (
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${isDarkMode ? "bg-amber-900/30 text-amber-300" : "bg-amber-50 text-amber-700"}`}>
                {responsibilities.length} client action item{responsibilities.length > 1 ? "s" : ""}
              </div>
            )}
            {summary.case_agent && (
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${isDarkMode ? "bg-zinc-800 text-zinc-300" : "bg-slate-100 text-slate-700"}`}>
                <Phone className="h-3 w-3" />
                {summary.case_agent}{summary.case_agent_phone ? ` — ${summary.case_agent_phone}` : ""}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ClientAIAssistant = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    fetchClientAndHistory();
  }, []);

  const fetchClientAndHistory = async () => {
    try {
      const res = await authFetch(`${API}/clients/me`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        if (data?.client_id) {
          setClientId(data.client_id);
          loadHistory(data.client_id);
          fetchSummary(data.client_id);
        }
      }
    } catch {}
  };

  const fetchSummary = async (cid) => {
    setSummaryLoading(true);
    try {
      const res = await authFetch(`${API}/clients/${cid}/crm-summary`);
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch {} finally {
      setSummaryLoading(false);
    }
  };

  const loadHistory = async (cid) => {
    try {
      const res = await authFetch(`${API}/clients/${cid}/chat-history`);
      if (res.ok) {
        const data = await res.json();
        setSessionId(data.session_id);
        if (data.messages?.length > 0) {
          setMessages(data.messages.map((m) => ({ role: m.role, content: m.content })));
        }
      }
    } catch {}
  };

  const sendMessage = async () => {
    if (!input.trim() || loading || !clientId) return;
    const userMsg = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMsg }]);
    setLoading(true);

    try {
      const res = await authFetch(`${API}/clients/${clientId}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: userMsg,
          session_id: sessionId,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setSessionId(data.session_id);
        setMessages((prev) => [...prev, { role: "assistant", content: data.response }]);
      } else {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: "Sorry, I couldn't process that request. Please try again." },
        ]);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Connection error. Please try again." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className={`flex min-h-screen transition-colors duration-300 ${isDarkMode ? "bg-black" : "bg-slate-50"}`}>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isClient={true} />

      <main className="flex-1 min-w-0 md:ml-64 flex flex-col h-screen">
        <header className="header-glass sticky top-0 z-30 px-4 md:px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden p-2 hover:bg-slate-100 rounded-lg"
              data-testid="mobile-menu-toggle"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div>
              <h1 className={`text-xl md:text-2xl font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>
                CFI Assistant
              </h1>
              <p className={`text-sm ${isDarkMode ? "text-zinc-400" : "text-slate-500"}`}>
                Ask questions about your credit file, billing, and progress
              </p>
            </div>
          </div>
        </header>

        <div className="flex-1 flex flex-col p-4 md:p-6 max-w-3xl mx-auto w-full overflow-hidden">
          {/* Quick Summary Banner */}
          {summaryLoading && (
            <div className="flex items-center gap-2 mb-4">
              <Loader2 className="h-4 w-4 text-[#22c55e] animate-spin" />
              <span className={`text-sm ${isDarkMode ? "text-zinc-400" : "text-slate-500"}`}>Loading your account summary...</span>
            </div>
          )}
          {summary && <QuickSummaryBanner summary={summary} isDarkMode={isDarkMode} />}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto space-y-4 mb-4" data-testid="chat-messages">
            {messages.length === 0 && !summaryLoading && (
              <div className="text-center py-8">
                <Bot className={`h-12 w-12 mx-auto mb-3 ${isDarkMode ? "text-emerald-500" : "text-[#357c2a]"}`} />
                <h3 className={`text-lg font-semibold mb-1 ${isDarkMode ? "text-white" : "text-slate-900"}`}>
                  Welcome to CFI Assistant
                </h3>
                <p className={`text-sm ${isDarkMode ? "text-zinc-400" : "text-slate-500"}`}>
                  Ask me anything about your credit scores, billing, game plan, appointments, or restoration progress.
                </p>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                {msg.role === "assistant" && (
                  <div className="w-8 h-8 rounded-full bg-[#357c2a] flex items-center justify-center flex-shrink-0">
                    <Bot className="h-4 w-4 text-white" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] rounded-lg px-4 py-3 ${
                    msg.role === "user"
                      ? isDarkMode
                        ? "bg-emerald-700 text-white"
                        : "bg-[#357c2a] text-white"
                      : isDarkMode
                      ? "bg-zinc-800 text-zinc-200"
                      : "bg-white border border-slate-200 text-slate-800"
                  }`}
                >
                  {msg.role === "assistant" ? (
                    <MarkdownMessage>{msg.content}</MarkdownMessage>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  )}
                </div>
                {msg.role === "user" && (
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isDarkMode ? "bg-zinc-700" : "bg-slate-200"}`}>
                    <User className={`h-4 w-4 ${isDarkMode ? "text-white" : "text-slate-600"}`} />
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#357c2a] flex items-center justify-center flex-shrink-0">
                  <Bot className="h-4 w-4 text-white" />
                </div>
                <div className={`rounded-lg px-4 py-3 ${isDarkMode ? "bg-zinc-800" : "bg-white border border-slate-200"}`}>
                  <div className="flex gap-1">
                    <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                    <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className={`flex gap-2 items-end ${isDarkMode ? "border-zinc-800" : "border-slate-200"} border-t pt-4`}>
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about your scores, billing, game plan, next steps..."
              rows={2}
              className={`flex-1 resize-none ${isDarkMode ? "bg-zinc-800 border-zinc-700 text-white" : ""}`}
              data-testid="chat-input"
            />
            <Button
              onClick={sendMessage}
              disabled={!input.trim() || loading || !clientId}
              className="bg-[#357c2a] hover:bg-[#2d6a5d] text-white h-[60px] px-4"
              data-testid="chat-send-btn"
            >
              <Send className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ClientAIAssistant;
